import { useEffect, useMemo, useRef, useState } from 'react';
import { Task } from './types';

type Patch = Partial<Task> & { id: number };
type Field = { value: unknown; token: number; pending: boolean };
type Changes = Map<number, Map<keyof Task, Field>>;

// Track changed fields, rather than a snapshot of the whole board. Refreshes and
// completion of other saves must not erase a card's outstanding changes.
export const useOptimisticTasks = (tasks: Task[]) => {
  const changes = useRef<Changes>(new Map());
  const sequence = useRef(0);
  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;
  const [snapshot, setSnapshot] = useState<Changes>(new Map());
  const publish = () => setSnapshot(new Map(changes.current));

  const reconcile = () => {
    const byId = new Map(tasksRef.current.map(task => [task.id, task]));
    let changed = false;
    changes.current.forEach((fields, id) => {
      const task = byId.get(id);
      fields.forEach((field, key) => {
        if (!field.pending && (!task || task[key] === field.value)) {
          fields.delete(key);
          changed = true;
        }
      });
      if (!fields.size) changes.current.delete(id);
    });
    if (changed) publish();
  };

  useEffect(() => {
    reconcile();
    // Reconcile only when the authoritative list changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  const applyTaskPatches = (updates: Patch[]) => {
    const token = ++sequence.current;
    updates.forEach(({ id, ...patch }) => {
      const fields = changes.current.get(id) || new Map<keyof Task, Field>();
      (Object.keys(patch) as Array<Exclude<keyof Task, 'id'>>).forEach(key => {
        fields.set(key, { value: patch[key], token, pending: true });
      });
      changes.current.set(id, fields);
    });
    publish();
    return (saved: boolean) => {
      changes.current.forEach((fields, id) => {
        fields.forEach((field, key) => {
          if (field.token !== token) return;
          if (saved) field.pending = false;
          else fields.delete(key);
        });
        if (!fields.size) changes.current.delete(id);
      });
      reconcile();
      publish();
    };
  };

  const displayTasks = useMemo(() => tasks.map(task => {
    const fields = snapshot.get(task.id);
    if (!fields) return task;
    const patch: Partial<Task> = {};
    fields.forEach((field, key) => { (patch as Record<string, unknown>)[key] = field.value; });
    return { ...task, ...patch };
  }), [tasks, snapshot]);
  return { displayTasks, applyTaskPatches };
};
